import React, { useEffect, useRef, useState } from "react";
import { createChart, ColorType, LineStyle, CrosshairMode } from "lightweight-charts";
import { Activity, Loader2 } from "lucide-react";
import api from "../../lib/api";
import { fmtPrice, fmtPct } from "../../lib/format";
import { CryptoLogo } from "../common/CryptoBits";

const TIMEFRAMES = ["5m", "15m", "1h", "4h", "1d"];
const EMA_CONF = [
  { key: "ema20", label: "EMA 20", color: "#f59e0b" },
  { key: "ema50", label: "EMA 50", color: "#38bdf8" },
  { key: "ema200", label: "EMA 200", color: "#f472b6" },
];

export default function ChartPanel({ symbol, base, interval, setInterval, analysis, lockedPlan, livePrice, swingHits }) {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const seriesRef = useRef({});
  const lastCandleRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [emaOn, setEmaOn] = useState({ ema20: true, ema50: true, ema200: false });
  const [provider, setProvider] = useState("");
  const [candles, setCandles] = useState([]);

  // Create chart once
  useEffect(() => {
    if (!containerRef.current) return;
    const chart = createChart(containerRef.current, {
      localization: { locale: "en-US" },
      layout: { background: { type: ColorType.Solid, color: "transparent" }, textColor: "#8a8a95", fontFamily: "Inter" },
      grid: { vertLines: { color: "rgba(255,255,255,0.03)" }, horzLines: { color: "rgba(255,255,255,0.04)" } },
      rightPriceScale: { borderColor: "#232329", scaleMargins: { top: 0.08, bottom: 0.28 } },
      timeScale: { borderColor: "#232329", timeVisible: true, secondsVisible: false },
      crosshair: { mode: CrosshairMode.Normal, vertLine: { color: "#8b5cf6", labelBackgroundColor: "#6d28d9" }, horzLine: { color: "#8b5cf6", labelBackgroundColor: "#6d28d9" } },
      height: window.innerWidth < 640 ? 300 : 380,
    });
    const candle = chart.addCandlestickSeries({
      upColor: "#22c55e", downColor: "#ef4444", borderVisible: false,
      wickUpColor: "#22c55e", wickDownColor: "#ef4444",
    });
    const volume = chart.addHistogramSeries({ priceScaleId: "vol", priceFormat: { type: "volume" } });
    volume.priceScale().applyOptions({ scaleMargins: { top: 0.82, bottom: 0 } });

    seriesRef.current = { candle, volume, emas: {} };
    EMA_CONF.forEach((e) => {
      seriesRef.current.emas[e.key] = chart.addLineSeries({ color: e.color, lineWidth: 1.5, priceLineVisible: false, lastValueVisible: false });
    });
    chartRef.current = chart;

    const ro = new ResizeObserver(() => {
      if (containerRef.current)
        chart.applyOptions({
          width: containerRef.current.clientWidth,
          height: window.innerWidth < 640 ? 300 : 380,
        });
    });
    ro.observe(containerRef.current);
    chart.applyOptions({ width: containerRef.current.clientWidth });

    return () => { ro.disconnect(); chart.remove(); chartRef.current = null; };
  }, []);

  // Load candles when symbol/interval changes
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      try {
        const res = await api.getKlines(symbol, interval, 300);
        if (!mounted) return;
        setProvider(res.provider);
        setCandles(res.candles);
        lastCandleRef.current = res.candles.length ? { ...res.candles[res.candles.length - 1] } : null;
        const { candle, volume } = seriesRef.current;
        candle.setData(res.candles.map((c) => ({ time: c.time, open: c.open, high: c.high, low: c.low, close: c.close })));
        volume.setData(res.candles.map((c) => ({
          time: c.time, value: c.volume,
          color: c.close >= c.open ? "rgba(34,197,94,0.35)" : "rgba(239,68,68,0.35)",
        })));
        chartRef.current?.timeScale().fitContent();
      } catch (e) { /* noop */ } finally { if (mounted) setLoading(false); }
    };
    load();
    const poll = window.setInterval(load, 15000);
    return () => { mounted = false; window.clearInterval(poll); };
  }, [symbol, interval]);

  // EMA series update
  useEffect(() => {
    if (!candles.length || !analysis?.series) return;
    EMA_CONF.forEach((e) => {
      const series = seriesRef.current.emas[e.key];
      const values = analysis.series[e.key] || [];
      if (emaOn[e.key] && values.length === candles.length) {
        series.setData(candles.map((c, i) => ({ time: c.time, value: values[i] })));
      } else {
        series.setData([]);
      }
    });
  }, [candles, analysis, emaOn]);

  // Plan / SR price lines
  useEffect(() => {
    const candle = seriesRef.current.candle;
    if (!candle || !analysis) return;
    (candle._ymLines || []).forEach((l) => candle.removePriceLine(l));
    const lines = [];
    const add = (price, color, title, style = LineStyle.Dashed) => {
      if (price == null) return;
      lines.push(candle.createPriceLine({ price, color, lineWidth: 1, lineStyle: style, axisLabelVisible: true, title }));
    };
    // Consistency: when a locked 4H plan exists, the chart draws ITS levels (same
    // planId/entry/SL as Swing card & Risk Center), else the mechanical snapshot.
    const p = lockedPlan ? {
      entryLow: lockedPlan.entryLow, entryHigh: lockedPlan.entryHigh, stopLoss: lockedPlan.stopLoss,
      tp1: lockedPlan.tp1, tp2: lockedPlan.tp2, tp3: lockedPlan.tp3,
    } : (analysis.plan || {});
    add(analysis.indicators?.sr?.support, "#22d3ee", "SUP", LineStyle.Dotted);
    add(analysis.indicators?.sr?.resistance, "#f59e0b", "RES", LineStyle.Dotted);
    if (p.entryLow) add(p.entryLow, "#8b5cf6", "ENTRY");
    if (p.entryHigh) add(p.entryHigh, "#8b5cf6", "ENTRY");
    add(p.stopLoss, "#ef4444", "SL", LineStyle.Solid);
    add(p.tp1, "#22c55e", "TP1");
    add(p.tp2, "#16a34a", "TP2");
    add(p.tp3, "#15803d", "TP3");
    candle._ymLines = lines;
  }, [analysis, lockedPlan]);

  // Live tick: update the last candle's close from the streamed price
  useEffect(() => {
    if (!livePrice || !lastCandleRef.current || !seriesRef.current.candle) return;
    const lc = lastCandleRef.current;
    const updated = {
      time: lc.time,
      open: lc.open,
      high: Math.max(lc.high, livePrice),
      low: Math.min(lc.low, livePrice),
      close: livePrice,
    };
    lastCandleRef.current = updated;
    try { seriesRef.current.candle.update(updated); } catch (_) { /* ignore */ }
  }, [livePrice]);

  // Green check markers for TP hits on the chart (snapped to nearest candle)
  useEffect(() => {
    const candle = seriesRef.current.candle;
    if (!candle || !candles.length) return;
    if (!swingHits || !swingHits.length) { try { candle.setMarkers([]); } catch (_) {} return; }
    const times = candles.map((c) => c.time);
    const snap = (ts) => times.reduce((a, b) => (Math.abs(b - ts) < Math.abs(a - ts) ? b : a), times[0]);
    const markers = swingHits
      .filter((h) => h.at)
      .map((h) => {
        const ts = Math.floor(new Date(h.at).getTime() / 1000);
        const w = new Date(new Date(h.at).getTime() + 8 * 3600000);
        const hhmm = w.toISOString().slice(11, 16);
        return {
          time: snap(ts), position: "aboveBar", color: "#22c55e", shape: "arrowUp",
          text: `TP${h.tp} HIT ${hhmm} WITA`,
        };
      })
      .sort((a, b) => a.time - b.time);
    try { candle.setMarkers(markers); } catch (_) { /* ignore */ }
  }, [candles, swingHits]);

  const price = livePrice || analysis?.price;
  // Unified plan for the levels strip: locked 4H plan wins, else mechanical snapshot.
  const pl = lockedPlan ? {
    entryLow: lockedPlan.entryLow, entryHigh: lockedPlan.entryHigh, stopLoss: lockedPlan.stopLoss,
    tp1: lockedPlan.tp1, tp2: lockedPlan.tp2, tp3: lockedPlan.tp3,
    invalidation: lockedPlan.invalidationRule, planId: lockedPlan.planId, locked: true,
  } : (analysis?.plan || null);

  return (
    <div className="ym-panel p-4">
      {/* header */}
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <CryptoLogo base={base} size={30} />
        <div className="leading-tight">
          <div className="text-[15px] font-bold">{base}/USDT</div>
          <div className="flex items-center gap-1.5 text-[10px]" style={{ color: "var(--text-dim)" }}>
            <span>Spot</span><span>·</span><span style={{ color: "var(--accent-soft)" }}>{provider || "…"}</span>
          </div>
        </div>
        <div className="ml-1">
          <div className="text-[22px] font-bold tabnum leading-none">{price ? `$${fmtPrice(price)}` : "—"}</div>
        </div>

        {/* timeframe */}
        <div className="ml-auto flex items-center gap-1 p-0.5 rounded-lg" style={{ background: "var(--panel-2)", border: "1px solid var(--border)" }}>
          {TIMEFRAMES.map((tf) => (
            <button key={tf} onClick={() => setInterval(tf)}
              className="px-2.5 py-1 rounded-md text-[11px] font-semibold ym-hover"
              style={{ background: interval === tf ? "rgba(139,92,246,.25)" : "transparent", color: interval === tf ? "#fff" : "var(--text-muted)" }}>
              {tf.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {/* EMA toggles + SR summary */}
      <div className="flex flex-wrap items-center gap-2 mb-2">
        {EMA_CONF.map((e) => (
          <button key={e.key} onClick={() => setEmaOn((s) => ({ ...s, [e.key]: !s[e.key] }))}
            className="pill ym-hover" style={{ background: emaOn[e.key] ? "var(--panel-2)" : "transparent", borderColor: "var(--border)", color: emaOn[e.key] ? e.color : "var(--text-dim)" }}>
            <span style={{ width: 8, height: 2, background: e.color, display: "inline-block", opacity: emaOn[e.key] ? 1 : 0.3 }} />
            {e.label}
          </button>
        ))}
        {analysis?.indicators && (
          <div className="ml-auto flex items-center gap-3 text-[11px]" style={{ color: "var(--text-muted)" }}>
            <span>RSI <b className="tabnum" style={{ color: "var(--text)" }}>{analysis.indicators.rsi}</b></span>
            <span>ATR <b className="tabnum" style={{ color: "var(--text)" }}>{fmtPrice(analysis.indicators.atr)}</b></span>
          </div>
        )}
      </div>

      <div className="relative rounded-lg overflow-hidden" style={{ background: "rgba(0,0,0,0.2)" }}>
        {loading && (
          <div className="absolute inset-0 z-10 grid place-items-center" style={{ background: "rgba(8,8,10,.5)" }}>
            <Loader2 className="animate-spin" size={22} style={{ color: "var(--accent-soft)" }} />
          </div>
        )}
        <div ref={containerRef} />
      </div>

      {/* Levels strip */}
      {pl && (
        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
          <Level label={pl.locked ? "Entry (locked)" : "Entry"} value={pl.entryLow ? `${fmtPrice(pl.entryLow)}–${fmtPrice(pl.entryHigh)}` : "—"} color="#8b5cf6" />
          <Level label="Stop Loss" value={pl.stopLoss ? fmtPrice(pl.stopLoss) : "—"} color="#ef4444" />
          <Level label="TP1" value={pl.tp1 ? fmtPrice(pl.tp1) : "—"} color="#22c55e" />
          <Level label="TP2" value={pl.tp2 ? fmtPrice(pl.tp2) : "—"} color="#16a34a" />
          <Level label="TP3" value={pl.tp3 ? fmtPrice(pl.tp3) : "—"} color="#15803d" />
          <Level label="Support" value={fmtPrice(analysis.indicators.sr.support)} color="#22d3ee" />
          <Level label="Resistance" value={fmtPrice(analysis.indicators.sr.resistance)} color="#f59e0b" />
        </div>
      )}
      {pl?.invalidation && (
        <div className="mt-2 flex items-start gap-2 text-[11px] rounded-lg p-2.5" style={{ background: "rgba(239,68,68,.06)", border: "1px solid rgba(239,68,68,.2)", color: "var(--text-muted)" }}>
          <Activity size={13} style={{ color: "var(--red)", marginTop: 1 }} />
          <span><b style={{ color: "var(--red)" }}>{pl.locked ? `Invalidasi · ${pl.planId}:` : "Invalidasi:"}</b> {pl.invalidation}</span>
        </div>
      )}
    </div>
  );
}

function Level({ label, value, color }) {
  return (
    <div className="rounded-lg px-2.5 py-2" style={{ background: "var(--panel-2)", border: "1px solid var(--border)" }}>
      <div className="text-[9px] font-semibold tracking-wide flex items-center gap-1" style={{ color: "var(--text-dim)" }}>
        <span style={{ width: 6, height: 6, borderRadius: 2, background: color, display: "inline-block" }} />{label.toUpperCase()}
      </div>
      <div className="text-[12px] font-bold tabnum mt-0.5">{value}</div>
    </div>
  );
}
